/**
 * Lot codes: 6 random Crockford base32 characters plus a Luhn mod 32 check character, shown as T030-24S
 * (mirrors backend/common/codes.py). QR labels carry `<base>/l/<code>`; scanning accepts that URL, a bare
 * code, lower case, separators and the Crockford aliases I/L → 1 and O → 0.
 */
const ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const CODE_LENGTH = 7;
const ALIASES: Record<string, string> = { I: "1", L: "1", O: "0" };

export function normalizeCode(text: string): string {
  return text
    .replace(/[\s\-_.]+/g, "")
    .toUpperCase()
    .replace(/[ILO]/g, (char) => ALIASES[char] ?? char);
}

export function isValidCode(code: string): boolean {
  if (code.length !== CODE_LENGTH || [...code].some((char) => !ALPHABET.includes(char))) return false;
  const n = ALPHABET.length;
  let factor = 1;
  let total = 0;
  for (const char of [...code].reverse()) {
    const addend = factor * ALPHABET.indexOf(char);
    factor = factor === 1 ? 2 : 1;
    total += Math.floor(addend / n) + (addend % n);
  }
  return total % n === 0;
}

export function displayCode(code: string): string {
  return code.length === CODE_LENGTH ? `${code.slice(0, 4)}-${code.slice(4)}` : code;
}

/** The lot code in a scanned text (a `/l/<code>` link or a bare code), or null when it isn't one. */
export function extractLotCode(scanned: string): string | null {
  const text = scanned.trim();
  const link = /\/l\/([0-9A-Za-z\-_.]+)\s*$/i.exec(text);
  const candidate = normalizeCode(link ? (link[1] ?? "") : text);
  return isValidCode(candidate) ? candidate : null;
}
